import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, PlusCircle, Package2, TicketPercent, Trash2 } from "lucide-react";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCatalogPackageTemplates } from "@/hooks/useCatalogPackages";
import { usePatientPackages } from "@/hooks/usePatientPackages";
import type { AppointmentPatientPackageBillingIntent } from "@/hooks/useAppointmentPatientPackage";
import { cn } from "@/lib/utils";
import {
  BILLING_INTENT_BADGE_CLASS,
  BILLING_INTENT_LABELS,
} from "@/lib/appointment-billing-intent";
import { isItemAllowedForBehavior } from "@/lib/appointments/availability";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";

export type AppointmentPackageDraft = {
  id?: string;
  mode: "new" | "existing";
  petId: string;
  catalogPackageId: string | null;
  patientPackageId: string | null;
  sessionsToConsume: number;
  title?: string;
  price?: number;
  amountPaid?: number;
  amountOutstanding?: number;
  financialStatus?: "pendente" | "parcial" | "pago" | null;
  controlsSessions?: boolean;
  remainingSessions?: number | null;
  totalSessions?: number | null;
  billingIntent: AppointmentPatientPackageBillingIntent;
  suggestedChargeAmount?: number | null;
  allowedAppointmentTypes?: string[];
};

interface AppointmentPackageSectionProps {
  clinicId: string;
  petId?: string | null;
  values: AppointmentPackageDraft[];
  appointmentType: string;
  disabled?: boolean;
  isRecurring?: boolean;
  readOnly?: boolean;
  onChange: (next: AppointmentPackageDraft[]) => void;
}

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const getModeBadge = (mode: AppointmentPackageDraft["mode"], t: (key: string, options?: Record<string, unknown>) => string) => {
  if (mode === "new") return { label: t("packages.newPackage"), className: "bg-orange-100 text-orange-700 hover:bg-orange-100" };
  return { label: t("packages.useExisting"), className: "bg-sky-100 text-sky-700 hover:bg-sky-100" };
};

const getBillingIntentBadge = (intent: AppointmentPatientPackageBillingIntent) => {
  return {
    label: BILLING_INTENT_LABELS[intent],
    className: BILLING_INTENT_BADGE_CLASS[intent],
  };
};

const normalizeMoney = (value: string) => {
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const getPerSessionAmount = (price?: number | null, sessions?: number | null) => {
  const safePrice = Math.max(0, Number(price ?? 0));
  const safeSessions = Math.max(1, Number(sessions ?? 1));
  return safePrice / safeSessions;
};

const getSessionProgressLabel = (
  draft: AppointmentPackageDraft,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (draft.controlsSessions === false) {
    return null;
  }
  const totalSessions = Math.max(1, Number(draft.totalSessions ?? 1));

  if (draft.mode === "new") {
    const currentSession = Math.min(Math.max(1, Number(draft.sessionsToConsume ?? 1)), totalSessions);
    return t("packages.sessionProgress", { current: currentSession, total: totalSessions });
  }

  const remainingSessions = Math.max(0, Number(draft.remainingSessions ?? 0));
  const usedSessions = Math.max(0, totalSessions - remainingSessions);
  const currentSession = Math.min(usedSessions + 1, totalSessions);
  return t("packages.sessionProgress", { current: currentSession, total: totalSessions });
};

const getCurrentChargeLabel = (draft: AppointmentPackageDraft) => {
  return formatCurrency(draft.suggestedChargeAmount ?? 0);
};

const getRemainingAmountLabel = (draft: AppointmentPackageDraft) => {
  if (draft.mode === "new") {
    const packageTotal = Math.max(0, Number(draft.price ?? 0));
    return formatCurrency(Math.max(0, packageTotal));
  }

  return formatCurrency(Math.max(0, Number(draft.amountOutstanding ?? 0)));
};

const getOutstandingAmount = (draft: {
  amountOutstanding?: number | null;
  outstanding_amount?: number | null;
  price?: number | null;
}) => Math.max(0, Number(draft.amountOutstanding ?? draft.outstanding_amount ?? draft.price ?? 0));

const isSettledPackage = (draft: {
  amountOutstanding?: number | null;
  outstanding_amount?: number | null;
  financialStatus?: "pendente" | "parcial" | "pago" | null;
  status_financeiro?: "pendente" | "parcial" | "pago" | null;
  price?: number | null;
}) =>
  (draft.financialStatus === "pago" || draft.status_financeiro === "pago") ||
  getOutstandingAmount(draft) <= 0;

const getSettledBadge = (t: (key: string) => string) => ({
  label: t("financialSettled"),
  className: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
});

const getSuggestedExistingChargeAmount = (
  draft: {
    amountOutstanding?: number | null;
    outstanding_amount?: number | null;
    remainingSessions?: number | null;
    sessions_remaining?: number | null;
  },
  intent: AppointmentPatientPackageBillingIntent,
  partialChargeAmount: string,
) => {
  const outstandingAmount = getOutstandingAmount(draft);
  if (intent === "integral_now") return outstandingAmount;
  if (intent === "partial_now") {
    return Math.min(outstandingAmount, normalizeMoney(partialChargeAmount));
  }
  return 0;
};

export function AppointmentPackageSection({
  clinicId,
  petId,
  values,
  appointmentType,
  disabled,
  isRecurring,
  readOnly,
  onChange,
}: AppointmentPackageSectionProps) {
  const { t } = useTranslation("calendar");
  const { data: catalogPackages = [], isLoading: isLoadingCatalog } = useCatalogPackageTemplates();
  const { data: patientPackages = [], isLoading: isLoadingPatientPackages } = usePatientPackages(petId, clinicId);
  const [mode, setMode] = useState<"new" | "existing">("existing");
  const [searchValue, setSearchValue] = useState("");
  const [selectedOptionId, setSelectedOptionId] = useState<string>("");
  const [sessionsToConsume, setSessionsToConsume] = useState<number>(1);
  const [billingIntent, setBillingIntent] = useState<AppointmentPatientPackageBillingIntent>("undecided");
  const [partialChargeAmount, setPartialChargeAmount] = useState<string>("");

  const isBusy = isLoadingCatalog || isLoadingPatientPackages;
  const hasNoExistingPackages = mode === "existing" && patientPackages.length === 0;
  const isInteractionDisabled = Boolean(disabled || readOnly);
  const selectedExistingPackageIds = useMemo(
    () => new Set(values.filter((draft) => draft.mode === "existing" && draft.patientPackageId).map((draft) => draft.patientPackageId as string)),
    [values],
  );
  const selectedCatalogPackageIds = useMemo(
    () => new Set(values.filter((draft) => draft.mode === "new" && draft.catalogPackageId).map((draft) => draft.catalogPackageId as string)),
    [values],
  );

  const availableOptions = useMemo<AutocompleteOption[]>(() => {
    if (mode === "new") {
      return catalogPackages
        .filter((pkg) => isItemAllowedForBehavior(pkg.allowed_appointment_types, appointmentType))
        .filter((pkg) => !selectedCatalogPackageIds.has(pkg.id))
        .map((pkg) => ({
          value: pkg.id,
          label: pkg.item_name,
        }));
    }

    return patientPackages
      .filter((pkg) => isItemAllowedForBehavior(pkg.catalog_package?.item?.allowed_appointment_types, appointmentType))
      .filter((pkg) => !selectedExistingPackageIds.has(pkg.id))
      .map((pkg) => ({
        value: pkg.id,
        label: pkg.catalog_package?.item?.name ?? t("packages.packageLabel"),
      }));
  }, [appointmentType, catalogPackages, mode, patientPackages, selectedCatalogPackageIds, selectedExistingPackageIds, t]);

  const selectedCatalogPackage = useMemo(
    () => catalogPackages.find((pkg) => pkg.id === selectedOptionId) ?? null,
    [catalogPackages, selectedOptionId],
  );
  const selectedPatientPackage = useMemo(
    () => patientPackages.find((pkg) => pkg.id === selectedOptionId) ?? null,
    [patientPackages, selectedOptionId],
  );
  const selectedExistingPackageIsSettled = useMemo(
    () => (selectedPatientPackage ? isSettledPackage(selectedPatientPackage) : false),
    [selectedPatientPackage],
  );
  const selectedControlsSessions = mode === "new"
    ? (selectedCatalogPackage?.controls_sessions ?? true)
    : (selectedPatientPackage?.catalog_package?.controls_sessions ?? true);
  const incompatibleDrafts = useMemo(() => {
    return values.filter((draft) => {
      const allowedAppointmentTypes =
        draft.allowedAppointmentTypes ??
        (draft.mode === "new"
          ? catalogPackages.find((pkg) => pkg.id === draft.catalogPackageId)?.allowed_appointment_types
          : patientPackages.find((pkg) => pkg.id === draft.patientPackageId)?.catalog_package?.item?.allowed_appointment_types);

      return !isItemAllowedForBehavior(allowedAppointmentTypes, appointmentType);
    });
  }, [appointmentType, catalogPackages, patientPackages, values]);

  useEffect(() => {
    if (mode === "existing" && selectedExistingPackageIsSettled) return;
    if (billingIntent !== "partial_now") return;

    if (mode === "new" && selectedCatalogPackage) {
      setPartialChargeAmount(
        getPerSessionAmount(
          selectedCatalogPackage.sale_price,
          selectedCatalogPackage.default_sessions,
        ).toFixed(2),
      );
      return;
    }

    if (mode === "existing" && selectedPatientPackage) {
      setPartialChargeAmount(
        getPerSessionAmount(
          selectedPatientPackage.outstanding_amount,
          selectedPatientPackage.sessions_remaining,
        ).toFixed(2),
      );
    }
  }, [billingIntent, mode, selectedCatalogPackage, selectedExistingPackageIsSettled, selectedPatientPackage]);

  useEffect(() => {
    if (!selectedOptionId) return;
    if (availableOptions.some((option) => option.value === selectedOptionId)) return;
    setSelectedOptionId("");
    setSearchValue("");
  }, [availableOptions, selectedOptionId]);

  const handleAddPackage = () => {
    if (!petId) return;
    if (!selectedOptionId) return;

    if (mode === "new") {
      if (!selectedCatalogPackage) return;

      const fullPrice = selectedCatalogPackage.sale_price ?? 0;
      const effectiveBillingIntent = selectedCatalogPackage.controls_sessions ? billingIntent : "integral_now";
      const suggestedChargeAmount =
        effectiveBillingIntent === "integral_now"
          ? fullPrice
          : effectiveBillingIntent === "partial_now"
            ? normalizeMoney(partialChargeAmount)
            : 0;

      if (effectiveBillingIntent === "partial_now" && suggestedChargeAmount <= 0) {
        return;
      }

      onChange([
        ...values,
        {
          mode: "new",
          petId,
          catalogPackageId: selectedCatalogPackage.id,
          patientPackageId: null,
          sessionsToConsume: selectedCatalogPackage.controls_sessions ? Math.max(1, sessionsToConsume) : 1,
          title: selectedCatalogPackage.item_name,
          price: selectedCatalogPackage.sale_price,
          amountPaid: suggestedChargeAmount,
          financialStatus: "pendente",
          controlsSessions: selectedCatalogPackage.controls_sessions,
          remainingSessions: selectedCatalogPackage.default_sessions,
          totalSessions: selectedCatalogPackage.default_sessions,
          billingIntent: effectiveBillingIntent,
          suggestedChargeAmount,
          amountOutstanding: selectedCatalogPackage.sale_price ?? 0,
          allowedAppointmentTypes: selectedCatalogPackage.allowed_appointment_types,
        },
      ]);
    } else {
      if (!selectedPatientPackage) return;
      const effectiveBillingIntent = selectedPatientPackage.catalog_package?.controls_sessions
        ? (selectedExistingPackageIsSettled ? "undecided" : billingIntent)
        : "integral_now";
      const suggestedChargeAmount = getSuggestedExistingChargeAmount(
        {
          amountOutstanding: selectedPatientPackage.outstanding_amount,
          remainingSessions: selectedPatientPackage.sessions_remaining,
        },
        effectiveBillingIntent,
        partialChargeAmount,
      );
      if (!selectedExistingPackageIsSettled && effectiveBillingIntent === "partial_now" && suggestedChargeAmount <= 0) {
        return;
      }
      onChange([
        ...values,
        {
          mode: "existing",
          petId,
          catalogPackageId: null,
          patientPackageId: selectedPatientPackage.id,
          sessionsToConsume: selectedPatientPackage.catalog_package?.controls_sessions ? Math.max(1, sessionsToConsume) : 1,
          title: selectedPatientPackage.catalog_package?.item?.name ?? t("packages.packageLabel"),
          price: selectedPatientPackage.catalog_package?.item?.sale_price ?? 0,
          amountPaid: selectedPatientPackage.amount_paid,
          amountOutstanding: selectedPatientPackage.outstanding_amount,
          financialStatus: selectedPatientPackage.status_financeiro,
          controlsSessions: selectedPatientPackage.catalog_package?.controls_sessions ?? true,
          remainingSessions: selectedPatientPackage.sessions_remaining,
          totalSessions: selectedPatientPackage.sessions_total,
          billingIntent: effectiveBillingIntent,
          suggestedChargeAmount,
          allowedAppointmentTypes: selectedPatientPackage.catalog_package?.item?.allowed_appointment_types ?? [],
        },
      ]);
    }

    setSearchValue("");
    setSelectedOptionId("");
    setSessionsToConsume(1);
    setBillingIntent("undecided");
    setPartialChargeAmount("");
  };

  const removeDraft = (index: number) => {
    onChange(values.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <section className="rounded-lg border border-border/60 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-foreground">{t("packages.title")}</p>
        </div>
      </div>

      {incompatibleDrafts.length > 0 ? (
        <Alert className="mt-3 border-amber-200 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("packages.compatibilityWarningTitle")}</AlertTitle>
          <AlertDescription>
            {t("packages.compatibilityWarningDescription", {
              names: incompatibleDrafts.map((draft) => draft.title ?? t("packages.packageLabel")).join(", "),
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {!petId ? (
        <div className="mt-3 rounded-md border border-dashed border-border/60 p-3 text-sm text-muted-foreground">
          {t("packages.selectPatient")}
        </div>
      ) : isRecurring ? (
        <div className="mt-3 rounded-md border border-dashed border-border/60 p-3 text-sm text-muted-foreground">
          {t("packages.recurringUnavailable")}
        </div>
      ) : isBusy ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="mt-3 space-y-4 text-sm">
          {!isInteractionDisabled ? (
            <div className="rounded-lg border border-dashed bg-muted/30 p-3">
              <RadioGroup
                value={mode}
                onValueChange={(next) => {
                  setMode(next as "new" | "existing");
                  setSearchValue("");
                  setSelectedOptionId("");
                }}
                className="grid grid-cols-2 gap-2"
                disabled={disabled}
              >
                <label className={cn("flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2", mode === "existing" && "border-primary bg-orange-50/50")}>
                  <RadioGroupItem value="existing" id="appointment-package-existing" />
                  <span className="text-sm font-medium text-foreground">{t("packages.useExisting")}</span>
                </label>
                <label className={cn("flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2", mode === "new" && "border-primary bg-orange-50/50")}>
                  <RadioGroupItem value="new" id="appointment-package-new" />
                  <span className="text-sm font-medium text-foreground">{t("packages.newPackage")}</span>
                </label>
              </RadioGroup>

              <div className="mt-3 grid gap-3 sm:grid-cols-[1.5fr,0.7fr] sm:items-end">
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Autocomplete
                          options={availableOptions}
                          value={searchValue}
                          contentWidth="content"
                          popoverPortalled={false}
                          onChange={(value) => {
                            setSearchValue(value);
                            const matched = availableOptions.find((option) => option.label.toLowerCase() === value.toLowerCase());
                            setSelectedOptionId(matched?.value ?? "");
                          }}
                          onSelect={(option) => {
                            setSelectedOptionId(option.value);
                            setSearchValue(option.label);
                          }}
                          selectedValue={selectedOptionId}
                          placeholder={mode === "new" ? t("packages.searchCatalog") : t("packages.searchPatient")}
                          emptyMessage={mode === "new" ? t("packages.emptyCatalog") : t("packages.emptyPatient")}
                          renderOption={(option, isSelected) => {
                            const catalogPackage = mode === "new"
                              ? catalogPackages.find((pkg) => pkg.id === option.value)
                              : null;
                            const patientPackage = mode === "existing"
                              ? patientPackages.find((pkg) => pkg.id === option.value)
                              : null;
                            const badge = mode === "new"
                              ? { label: t("packages.newBadge"), className: "bg-orange-100 text-orange-700 hover:bg-orange-100" }
                              : isSettledPackage({
                                amountOutstanding: patientPackage?.outstanding_amount,
                                financialStatus: patientPackage?.status_financeiro,
                              })
                                ? getSettledBadge(t)
                                : null;
                            const remainingLabel = isSettledPackage({
                              amountOutstanding: patientPackage?.outstanding_amount,
                              financialStatus: patientPackage?.status_financeiro,
                            })
                              ? t("financialSettled")
                              : formatCurrency(patientPackage?.outstanding_amount ?? 0);

                            return (
                              <>
                                <Check className={cn("mr-2 h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="truncate font-medium">{option.label}</span>
                                    {badge ? <Badge className={badge.className}>{badge.label}</Badge> : null}
                                  </div>
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    {mode === "new"
                                      ? `${catalogPackage?.controls_sessions === false
                                        ? t("packages.charge")
                                        : t("packages.sessionCount", { count: catalogPackage?.default_sessions ?? 1 })} · ${formatCurrency(catalogPackage?.sale_price ?? 0)}`
                                      : `${patientPackage?.catalog_package?.controls_sessions === false
                                        ? t("packages.charge")
                                        : t("packages.balance", { remaining: patientPackage?.sessions_remaining ?? 0, total: patientPackage?.sessions_total ?? 0 })} · ${t("packages.remaining", { value: remainingLabel })}`}
                                  </p>
                                </div>
                              </>
                            );
                          }}
                          disabled={disabled || hasNoExistingPackages}
                        />
                      </div>
                    </TooltipTrigger>
                    {hasNoExistingPackages ? (
                      <TooltipContent side="top">
                        {t("packages.noPackagesForPatient")}
                      </TooltipContent>
                    ) : null}
                  </Tooltip>
                </TooltipProvider>
                {selectedControlsSessions ? (
                  <Input
                    type="number"
                    min={1}
                    value={sessionsToConsume}
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      setSessionsToConsume(Number.isNaN(value) ? 1 : Math.max(1, value));
                    }}
                    disabled={disabled || hasNoExistingPackages}
                  />
                ) : (
                  <Input value={t("packages.charge")} disabled />
                )}
              </div>

              {selectedControlsSessions && ((mode === "new" && selectedCatalogPackage) || (mode === "existing" && selectedPatientPackage && !selectedExistingPackageIsSettled)) ? (
                <div className="mt-3 space-y-3 rounded-md border border-border/60 bg-background p-3">
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t("packages.billingOptions")}
                    </p>
                    <Select
                      value={billingIntent}
                      onValueChange={(value) => setBillingIntent(value as AppointmentPatientPackageBillingIntent)}
                    >
                      <SelectTrigger disabled={disabled}>
                        <SelectValue placeholder={t("packages.billingIntentPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(BILLING_INTENT_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {billingIntent === "integral_now" ? (
                    <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
                      <span className="text-muted-foreground">{t("packages.suggestedAmountToday")} </span>
                      <span className="font-semibold text-foreground">
                        {formatCurrency(
                          mode === "new"
                            ? (selectedCatalogPackage?.sale_price ?? 0)
                            : (selectedPatientPackage?.outstanding_amount ?? 0),
                        )}
                      </span>
                    </div>
                  ) : null}

                  {billingIntent === "partial_now" ? (
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium text-foreground">{t("packages.chargeNow")}</p>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={partialChargeAmount}
                        onChange={(event) => setPartialChargeAmount(event.target.value)}
                        disabled={disabled}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <Button
                type="button"
                className="mt-3 w-full"
                onClick={handleAddPackage}
                disabled={
                  disabled ||
                  !selectedOptionId ||
                  (billingIntent === "partial_now" && normalizeMoney(partialChargeAmount) <= 0)
                }
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                {t("packages.addPackage")}
              </Button>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
              {t("packages.readOnlyMessage")}
            </div>
          )}

          <Separator />

          <div className="space-y-3">
            {values.length > 0 ? (
              values.map((draft, index) => {
                const modeBadge = getModeBadge(draft.mode, t);
                const billingIntentBadge = getBillingIntentBadge(draft.billingIntent);
                const settledBadge = draft.mode === "existing" && isSettledPackage(draft) ? getSettledBadge(t) : null;

                return (
                  <div
                    key={`${draft.mode}-${draft.catalogPackageId ?? draft.patientPackageId ?? index}-${index}`}
                    className={cn(
                      "rounded-md border p-3",
                    )}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold leading-none text-foreground">{draft.title ?? t("packages.packageLabel")}</p>
                          {(draft.mode === "new" || draft.billingIntent !== "undecided") ? (
                            <Badge className={billingIntentBadge.className}>{billingIntentBadge.label}</Badge>
                          ) : null}
                          {settledBadge ? (
                            <Badge className={settledBadge.className}>{settledBadge.label}</Badge>
                          ) : null}
                        </div>
                        <div className="mt-2 grid gap-1 text-sm text-muted-foreground">
                          {getSessionProgressLabel(draft, t) ? <span>{getSessionProgressLabel(draft, t)}</span> : null}
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <span>
                              {draft.controlsSessions === false || draft.mode === "new"
                                ? t("packages.charge")
                                : t("packages.sessionCharge")}:
                              <span className="font-medium text-foreground"> {getCurrentChargeLabel(draft)}</span>
                            </span>
                            <span>
                              {t("packages.totalToPay")}: <span className="font-medium text-foreground">{getRemainingAmountLabel(draft)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDraft(index)}
                        disabled={isInteractionDisabled}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-md border border-dashed border-border/60 p-4 text-sm text-muted-foreground">
                {t("packages.emptySelection")}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
